/* eslint-disable max-lines */
import { initialState } from "./initialState";
import { IQrCodeAction, IReducerResponse, QrCodeActions } from "./type";

export const reducer = (state: IReducerResponse, action: IQrCodeAction): IReducerResponse => {
    switch (action.type) {
        case QrCodeActions.TRACK_SCREEN_LOADING: {
            return {
                ...state,
                track: {
                    ...state.track,
                    loading: true,
                    error: false,
                },
            };
        }
        case QrCodeActions.TRACK_SCREEN_SUCCESS: {
            return {
                ...state,
                track: {
                    lastUpdated: action.payload.lastUpdated ?? Date.now(),
                    loading: false,
                    error: false,
                    data: action.payload.data,
                },
            };
        }
        case QrCodeActions.TRACK_SCREEN_RESET: {
            return {
                ...state,
                track: {
                    ...initialState.track,
                },
            };
        }
        case QrCodeActions.TRACK_SCREEN_ERROR: {
            return {
                ...state,
                track: {
                    ...state.track,
                    loading: false,
                    error: true,
                },
            };
        }

        case QrCodeActions.BILL_SUMMARY_LOADING: {
            return {
                ...state,
                billSummary: {
                    ...state.billSummary,
                    loading: true,
                    error: false,
                },
            };
        }
        case QrCodeActions.BILL_SUMMARY_SUCCESS: {
            return {
                ...state,
                billSummary: {
                    ...state.billSummary,
                    lastUpdated: Date.now(),
                    loading: false,
                    error: false,
                    data: action.payload.data,
                },
            };
        }
        case QrCodeActions.BILL_SUMMARY_ERROR: {
            return {
                ...state,
                billSummary: {
                    ...state.billSummary,
                    loading: false,
                    error: true,
                    errorInfo: action.payload?.errorInfo || {},
                },
            };
        }

        case QrCodeActions.MENU_SCREEN_LOADING: {
            return {
                ...state,
                menu: {
                    ...state.menu,
                    loading: true,
                    error: false,
                },
            };
        }
        case QrCodeActions.MENU_SCREEN_RESET: {
            // Reset menu state for refetch scenarios
            return {
                ...state,
                menu: {
                    ...state.menu,
                    loading: true,
                    error: false,
                    initialData: [],
                    filteredData: [],
                    parentType: "",
                    categoryId: "",
                    preference: "",
                    emptyItemCard: false,
                    appliedFilters: undefined,
                },
            };
        }
        case QrCodeActions.MENU_SCREEN_SUCCESS: {
            return {
                ...state,
                menu: {
                    ...state.menu,
                    loading: false,
                    error: false,
                    requestId: action.payload.requestId || "",
                },
            };
        }
        case QrCodeActions.MENU_SCREEN_ERROR: {
            return {
                ...state,
                menu: {
                    ...state.menu,
                    loading: false,
                    error: true,
                },
            };
        }

        case QrCodeActions.CART_SCREEN_LOADING: {
            return {
                ...state,
                cart: {
                    ...state.cart,
                    loading: true,
                    error: false,
                },
            };
        }
        case QrCodeActions.CART_SCREEN_SUCCESS: {
            return {
                ...state,
                cart: {
                    ...state.cart,
                    lastUpdated: Date.now(),
                    loading: false,
                    error: false,
                    data: action.payload.data,
                    isChildOrderExist: action.payload.isChildOrderExist ?? null,
                },
            };
        }
        case QrCodeActions.CART_SCREEN_ERROR: {
            return {
                ...state,
                cart: {
                    ...state.cart,
                    loading: false,
                    error: true,
                },
            };
        }

        case QrCodeActions.CART_UPDATE_LOADING: {
            return {
                ...state,
                cart: {
                    ...state.cart,
                    loading: true,
                },
            };
        }
        case QrCodeActions.CART_UPDATE_SUCCESS: {
            return {
                ...state,
                cart: {
                    ...state.cart,
                    lastUpdated: Date.now(),
                    loading: false,
                    error: false,
                    data: action.payload.response,
                },
            };
        }
        case QrCodeActions.CART_UPDATE_ERROR: {
            return {
                ...state,
                cart: {
                    ...state.cart,
                    loading: false,
                    error: true,
                },
            };
        }

        case QrCodeActions.PLACE_ORDER_ERROR: {
            return {
                ...state,
                placeOrderFailure: {
                    ...state.placeOrderFailure,
                    loading: false,
                    error: true,
                    data: action.payload.data,
                    isFullScreenError: action.payload.isFullScreenError || false,
                },
            };
        }

        case QrCodeActions.PLACE_ORDER_RESET: {
            return {
                ...state,
                placeOrderFailure: {
                    ...state.placeOrderFailure,
                    loading: false,
                    error: false,
                    isFullScreenError: false,
                    data: {
                        statusCode: 0,
                        success: {
                            cards: [],
                        },
                        data: { cards: [] },
                    },
                },
            };
        }

        case QrCodeActions.UPDATE_LOCAL_CART: {
            const newCartItems: Array<{ menu_item_id?: string }> = action.payload?.cartItems ?? [];
            const newCartItemIds = new Set(
                newCartItems.map((cartItem) => cartItem.menu_item_id).filter(Boolean)
            );
            // Clean up cross-sell entries for items no longer in the cart
            const filteredEntries = state.crossSell.entries.filter((e) =>
                newCartItemIds.has(e.itemId)
            );
            return {
                ...state,
                cart: {
                    ...state.cart,
                    error: false,
                    localCart: { ...state.cart.localCart, ...action.payload },
                },
                crossSell: {
                    ...state.crossSell,
                    entries: filteredEntries,
                },
            };
        }
        case QrCodeActions.CLEAR_LOCAL_CART: {
            return {
                ...state,
                cart: {
                    ...state.cart,
                    localCart: {
                        ...state.cart.localCart,
                        cartItems: [],
                        specialInstructions: "",
                    },
                },
                crossSell: {
                    ...state.crossSell,
                    entries: [],
                },
            };
        }

        case QrCodeActions.CURTAIN_SCREEN_LOADING: {
            return {
                ...state,
                curtain: {
                    ...state.curtain,
                    loading: true,
                    error: false,
                },
            };
        }
        case QrCodeActions.CURTAIN_SCREEN_SUCCESS: {
            return {
                ...state,
                curtain: {
                    lastUpdated: Date.now(),
                    loading: false,
                    error: false,
                    data: action.payload.data,
                },
            };
        }
        case QrCodeActions.CURTAIN_SCREEN_ERROR: {
            return {
                ...state,
                curtain: {
                    ...state.curtain,
                    loading: false,
                    error: true,
                },
            };
        }

        case QrCodeActions.PIN_GENERATION_LOADING: {
            return {
                ...state,
                pin: {
                    ...state.pin,
                    loading: true,
                    error: false,
                },
            };
        }
        case QrCodeActions.PIN_GENERATION_RESET: {
            // Reset pin state for refetch scenarios
            return {
                ...state,
                pin: {
                    loading: true,
                    error: false,
                    data: {
                        status: "",
                    },
                    lastUpdated: 0,
                },
            };
        }
        case QrCodeActions.PIN_GENERATION_SUCCESS: {
            return {
                ...state,
                pin: {
                    lastUpdated: Date.now(),
                    loading: false,
                    error: false,
                    data: action.payload.data,
                },
            };
        }
        case QrCodeActions.PIN_GENERATION_FAILURE: {
            return {
                ...state,
                pin: {
                    ...state.pin,
                    loading: false,
                    error: true,
                },
            };
        }
        case QrCodeActions.UPDATE_INITIAL_MENU: {
            return {
                ...state,
                menu: {
                    ...state.menu,
                    lastUpdated: Date.now(),
                    initialData: action.payload.data,
                    filteredData: action.payload.data,
                    categoryId: "",
                    preference: "",
                    parentType: "",
                    emptyItemCard: action.payload.emptyItemCard || false,
                    appliedFilters: undefined,
                },
            };
        }
        case QrCodeActions.MENU_SCREEN_UPDATE_REQUEST_ID: {
            return {
                ...state,
                menu: {
                    ...state.menu,
                    requestId: action.payload.requestId || "",
                },
            };
        }
        case QrCodeActions.MENU_SCREEN_UPDATE_CONFIGS: {
            return {
                ...state,
                menu: {
                    ...state.menu,
                    configs: action.payload.configs,
                },
            };
        }
        case QrCodeActions.UPDATE_FILTER_DATA: {
            return {
                ...state,
                menu: {
                    ...state.menu,
                    lastUpdated: Date.now(),
                    filteredData: action.payload.data,
                    appliedFilters: action.payload.appliedFilters || {},
                },
            };
        }
        case QrCodeActions.UPDATE_DATA_BY_PARENT_TYPE: {
            return {
                ...state,
                menu: {
                    ...state.menu,
                    filteredData: action.payload.filteredData,
                    parentType: action.payload.parentType,
                    categoryId: "",
                    emptyItemCard: action.payload.emptyItemCard || false,
                },
            };
        }
        case QrCodeActions.UPDATE_DATA_BY_CATEGORY: {
            return {
                ...state,
                menu: {
                    ...state.menu,
                    filteredData: action.payload.filteredData,
                    categoryId: action.payload.categoryId,
                    emptyItemCard: action.payload.emptyItemCard || false,
                },
            };
        }
        case QrCodeActions.UPDATE_DATA_PREFERENCE: {
            return {
                ...state,
                menu: {
                    ...state.menu,
                    filteredData: action.payload.filteredData,
                    preference: action.payload.preference,
                    emptyItemCard: action.payload.emptyItemCard || false,
                    appliedFilters: action.payload.appliedFilters || {},
                },
                crossSell: {
                    ...state.crossSell,
                    entries: [],
                },
            };
        }

        // Collections reducers
        case QrCodeActions.COLLECTIONS_SCREEN_LOADING: {
            const { collectionId } = action.payload;
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [collectionId]: {
                        ...state.collections[collectionId],
                        id: collectionId,
                        loading: true,
                        error: false,
                    },
                },
            };
        }
        case QrCodeActions.COLLECTIONS_SCREEN_SUCCESS: {
            const { collectionId } = action.payload;
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [collectionId]: {
                        ...state.collections[collectionId],
                        id: collectionId,
                        loading: false,
                        error: false,
                    },
                },
            };
        }
        case QrCodeActions.COLLECTIONS_SCREEN_ERROR: {
            const { collectionId } = action.payload;
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [collectionId]: {
                        ...state.collections[collectionId],
                        id: collectionId,
                        loading: false,
                        error: true,
                    },
                },
            };
        }
        case QrCodeActions.UPDATE_INITIAL_COLLECTIONS: {
            const { collectionId, data, emptyItemCard } = action.payload;
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [collectionId]: {
                        ...state.collections[collectionId],
                        id: collectionId,
                        lastUpdated: Date.now(),
                        initialData: data,
                        filteredData: data,
                        categoryId: "",
                        preference: "",
                        parentType: "",
                        emptyItemCard: emptyItemCard || false,
                        data: {
                            statusCode: 0,
                            success: { cards: data },
                            data: { cards: data },
                        },
                        loading: false,
                        error: false,
                    },
                },
            };
        }
        case QrCodeActions.UPDATE_COLLECTIONS_FILTER_DATA: {
            const { collectionId, data } = action.payload;
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [collectionId]: {
                        ...state.collections[collectionId],
                        id: collectionId,
                        lastUpdated: Date.now(),
                        filteredData: data,
                        appliedFilters: action.payload.appliedFilters || {},
                    },
                },
            };
        }
        case QrCodeActions.UPDATE_COLLECTIONS_BY_PARENT_TYPE: {
            const { collectionId, filteredData, parentType, emptyItemCard } = action.payload;
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [collectionId]: {
                        ...state.collections[collectionId],
                        id: collectionId,
                        filteredData,
                        parentType,
                        categoryId: "",
                        emptyItemCard: emptyItemCard || false,
                    },
                },
            };
        }
        case QrCodeActions.UPDATE_COLLECTIONS_PREFERENCE: {
            const { collectionId, filteredData, preference, emptyItemCard } = action.payload;
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [collectionId]: {
                        ...state.collections[collectionId],
                        id: collectionId,
                        filteredData,
                        preference,
                        emptyItemCard: emptyItemCard || false,
                        appliedFilters: action.payload.appliedFilters || {},
                    },
                },
            };
        }

        case QrCodeActions.SHOW_TOAST: {
            return {
                ...state,
                toast: {
                    show: true,
                    message: action.payload.message,
                    type: action.payload.type,
                    autoDismiss: action.payload.autoDismiss ?? true,
                    autoDismissTime: action.payload.autoDismissTime,
                },
            };
        }

        case QrCodeActions.DISMISS_TOAST: {
            return {
                ...state,
                toast: {
                    ...state.toast,
                    message: "",
                    show: false,
                },
            };
        }

        case QrCodeActions.SHOW_PAYMENT_FAILURE: {
            return {
                ...state,
                paymentFailure: {
                    show: true,
                    amount: action.payload.amount || "",
                    paymentFailureCardData: action.payload.paymentFailureCardData,
                },
            };
        }

        case QrCodeActions.HIDE_PAYMENT_FAILURE: {
            return {
                ...state,
                paymentFailure: {
                    ...state.paymentFailure,
                    show: false,
                    amount: "",
                    paymentFailureCardData: undefined,
                },
            };
        }

        case QrCodeActions.CROSS_SELL_ADD_ENTRY: {
            const { itemId, itemName, cartItemIdsAtOpen } = action.payload;
            // If this item is already showing as expanded, no-op (prevent re-trigger on quantity increment)
            const alreadyExpanded = state.crossSell.entries.some(
                (e) => e.itemId === itemId && e.isExpanded
            );
            if (alreadyExpanded) return state;
            // Replace all existing entries — adding from menu always starts a fresh cross-sell.
            // Animation for the outgoing panel is handled by leavingItemId in useCrossSellRenderer.
            return {
                ...state,
                crossSell: {
                    ...state.crossSell,
                    entries: [{ itemId, itemName, isExpanded: true, cartItemIdsAtOpen }],
                },
            };
        }

        case QrCodeActions.CROSS_SELL_REMOVE_ENTRY: {
            return {
                ...state,
                crossSell: {
                    ...state.crossSell,
                    entries: state.crossSell.entries.filter(
                        (e) => e.itemId !== action.payload.itemId
                    ),
                },
            };
        }

        case QrCodeActions.CROSS_SELL_CONFIG_LOADED: {
            return {
                ...state,
                crossSell: {
                    ...state.crossSell,
                    config: action.payload,
                },
            };
        }

        case QrCodeActions.CROSS_SELL_SET_SHOWN_ITEMS: {
            return {
                ...state,
                crossSell: {
                    ...state.crossSell,
                    shownItemIds: action.payload.shownItemIds,
                },
            };
        }

        default:
            return state;
    }
};
